/**
 * Mappers for converting API DTOs to dashboard view models.
 */

import { PrehireDto, StatusInfoDto } from '../../generated/api.onboarding.client';
import { toRollupStatus, RollupStatus } from '../../types';

/**
 * Dashboard Candidate view model used by the ManagerDashboard component.
 */
export interface DashboardCandidate {
    id: number;
    name: string;
    jobTitle: string;
    startDate: string;
    /** Start date as an epoch timestamp for numeric sorting; undefined when not set. */
    startDateValue?: number;
    location: string;
    currentStage: string;
    email: string;
    caseNumber: string;
    requisitionNumber: string;
    hrCaseNumber: string;
    completedSteps: number;
    totalSteps: number;
    hasInProgressSteps: boolean;
    daysToStart?: number;
    needsAttention?: boolean;
    hiringManagerEmail?: string;
    /** Raw statuses dictionary from the API, keyed by status area name */
    statuses?: { [key: string]: StatusInfoDto };
}

/**
 * Known onboarding status keys from the API.
 * The statuses dictionary on PrehireDto uses these keys.
 */
const ONBOARDING_STATUS_KEYS = [
    'BackgroundCheck',
    'Immigration',
    'I9Documents',
    'EmploymentAgreement',
    'EmployeeProfile',
    'StartDate',
    'MicrosoftAccount',
    'OfferAccepted',
];

/**
 * Counts how many required statuses are Completed out of the total required statuses.
 * Only statuses with isRequired === true are included.
 */
const countCompletedSteps = (statuses: { [key: string]: StatusInfoDto } | undefined): { completed: number; total: number; hasInProgress: boolean } => {
    if (!statuses) {
        return { completed: 0, total: 0, hasInProgress: false };
    }

    let completed = 0;
    let total = 0;
    let hasInProgress = false;

    for (const value of Object.values(statuses)) {
        if (!value.isRequired) continue;
        total++;
        const rollup = toRollupStatus(value.rollupStatus);
        if (rollup === RollupStatus.Completed) {
            completed++;
        } else if (rollup === RollupStatus.InProgress) {
            hasInProgress = true;
        }
    }

    return { completed, total, hasInProgress };
};

/**
 * Formats a Date as "Mon DD, YYYY" or returns "To be finalized" if missing.
 */
const formatStartDate = (date: Date | undefined): string => {
    if (!date) return 'To be finalized';
    const d = new Date(date);
    if (isNaN(d.getTime())) return 'To be finalized';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

/**
 * Returns the start date as an epoch timestamp for sorting, or undefined if missing/invalid.
 */
const toStartDateValue = (date: Date | undefined): number | undefined => {
    if (!date) return undefined;
    const d = new Date(date);
    return isNaN(d.getTime()) ? undefined : d.getTime();
};

/**
 * Calculates days until start date. Returns undefined if no start date.
 */
const calculateDaysToStart = (startDate: Date | undefined): number | undefined => {
    if (!startDate) return undefined;
    const start = new Date(startDate);
    if (isNaN(start.getTime())) return undefined;
    const now = new Date();
    const diffMs = start.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : undefined;
};

/**
 * Determines if a candidate needs attention — e.g. start date is approaching
 * but onboarding is not complete, or a status is Expired.
 */
const checkNeedsAttention = (
    statuses: { [key: string]: StatusInfoDto } | undefined,
    startDate: Date | undefined,
): boolean => {
    // Check for expired required statuses
    if (statuses) {
        const hasExpired = Object.values(statuses).some(
            s => s.isRequired && toRollupStatus(s.rollupStatus) === RollupStatus.Expired
        );
        if (hasExpired) return true;
    }

    // Check if start date is within 14 days and onboarding is incomplete
    if (startDate) {
        const daysToStart = calculateDaysToStart(startDate);
        if (daysToStart !== undefined && daysToStart <= 14) {
            const { completed, total } = countCompletedSteps(statuses);
            if (completed < total) return true;
        }
    }

    return false;
};

/**
 * Maps an array of PrehireDto from the API into DashboardCandidate view models.
 */
export const mapPrehiresToCandidates = (prehires: PrehireDto[]): DashboardCandidate[] => {
    return prehires.map((prehire, index) => {
        const { completed, total, hasInProgress } = countCompletedSteps(prehire.statuses);
        const startDate = prehire.candidate?.startDate;
        const needsAttention = checkNeedsAttention(prehire.statuses, startDate);
        const daysToStart = calculateDaysToStart(startDate);

        return {
            id: index + 1,
            name: prehire.candidate?.fullName || `${prehire.candidate?.firstName ?? ''} ${prehire.candidate?.lastName ?? ''}`.trim() || 'Unknown',
            jobTitle: prehire.candidate?.jobTitle || 'To be finalized',
            startDate: formatStartDate(startDate),
            startDateValue: toStartDateValue(startDate),
            location: [prehire.candidate?.workLocationCity, prehire.candidate?.geoRequisitionCountry].filter(Boolean).join(', ') || 'To be finalized',
            currentStage: Object.values(prehire.statuses ?? {}).find(s => toRollupStatus(s.rollupStatus) === RollupStatus.InProgress)?.status
                ?? (Object.values(prehire.statuses ?? {}).every(s => toRollupStatus(s.rollupStatus) === RollupStatus.Completed) && Object.keys(prehire.statuses ?? {}).length > 0 ? 'Completed' : 'Not Started'),
            email: prehire.candidate?.email || '',
            caseNumber: prehire.crmCaseId || 'To be finalized',
            requisitionNumber: prehire.candidate?.requisitionNumber || 'To be finalized',
            hrCaseNumber: prehire.crmCaseId || 'To be finalized',
            completedSteps: completed,
            totalSteps: total,
            hasInProgressSteps: hasInProgress,
            daysToStart: needsAttention ? daysToStart : undefined,
            needsAttention,
            hiringManagerEmail: prehire.hiringManager?.email || undefined,
            statuses: prehire.statuses ?? undefined,
        };
    });
};
